import { type ApolloError, gql, useMutation } from "@apollo/client";
import PQueue from "p-queue";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Transaction } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";

import { GET_ALL_TRANSACTIONS } from "./get-all";
import { SEARCH_TRANSACTION_INVOICES } from "./search-invoices";

const mutationQueue = new PQueue({ concurrency: 1 });

type DeleteInvoiceFromTransactionInput = {
  bankTxnIds: string[];
  invoiceIds: string[];
};

export const useDeleteInvoiceFromTransaction = (): [
  (input: DeleteInvoiceFromTransactionInput) => Promise<Transaction[]>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [deleteInvoiceFromTransaction, { loading, error }] = useMutation<{
    deleteInvoiceFromTransaction: Transaction[];
  }>(DELETE_INVOICE_FROM_TRANSACTION_MUTATION, {
    refetchQueries: [GET_ALL_TRANSACTIONS, SEARCH_TRANSACTION_INVOICES],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      if (!data?.deleteInvoiceFromTransaction) return;

      cache.evict({ fieldName: "searchTransactionInvoices" });
      cache.gc();
      cache.evict({ fieldName: "transactions" });
      cache.gc();
    },
  });

  const handleDeleteInvoiceFromTransaction = async (
    input: DeleteInvoiceFromTransactionInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    const invoiceIds = input.invoiceIds;

    try {
      const result = await mutationQueue.add(async () => {
        const { data: response } = await deleteInvoiceFromTransaction({
          variables: {
            bankTxnIds: input.bankTxnIds,
            invoiceIds,
          },
        });

        if (!response) {
          throw new Error("No response from deleteInvoiceFromTransaction");
        }

        toast.dismiss(toastId);

        return response.deleteInvoiceFromTransaction;
      });

      return result || [];
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleDeleteInvoiceFromTransaction, { loading, error }];
};

export const DELETE_INVOICE_FROM_TRANSACTION_MUTATION = gql`
  mutation DeleteInvoiceFromTransaction(
    $bankTxnIds: [String!]!
    $invoiceIds: [String]!
  ) {
    deleteInvoiceFromTransaction(
      bankTxnIds: $bankTxnIds
      invoiceIds: $invoiceIds
    ) {
      bankTxnId
      tenantId
      status
      amount
      currency
      createdAt
      counterpartyName
      counterpartyIban
      purpose
      executionDate
      side
      invoices {
        invoiceId
        invoiceNo
        totalAmount
        currency
        supplierName
        invoiceDate
      }
    }
  }
`;
